import { useEffect, useState, useCallback } from "react";
import { Pencil, Calendar } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import AdminHeader from "../../components/admin/AdminHeader";
import SlidePanel from "../../components/admin/SlidePanel";
import { TextField, TextareaField, FormFooterActions } from "../../components/admin/FormFields";
import ImageUpload from "../../components/admin/ImageUpload";

const EMPTY = {
  id: "",
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "NorthCrest Studio",
  tags: [],
};

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 80);

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tagsText, setTagsText] = useState("");

  const load = useCallback(() => api.get("/blog").then((r) => setItems(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  const open = (item) => {
    const target = item ? { ...item } : { ...EMPTY };
    setEditing(target);
    setTagsText((target.tags || []).join(", "));
  };
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.title || !editing.excerpt || !editing.cover_image) {
      toast.error("Title, Excerpt, and Cover Image are required");
      return;
    }
    setSaving(true);
    try {
      const tags = tagsText.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { ...editing, tags, slug: editing.slug || slugify(editing.title) };
      await api.post("/admin/blog", payload);
      toast.success("Post saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing.id) return close();
    if (!window.confirm(`Delete "${editing.title}"?`)) return;
    try {
      await api.delete(`/admin/blog/${editing.id}`);
      toast.success("Post deleted");
      close();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-blog">
      <AdminHeader
        title="Blog"
        subtitle="Editorial posts for the Journal section."
        action="Add Post"
        actionTestId="add-blog-btn"
        onAction={() => open(null)}
        count={items.length}
      />

      <div className="space-y-3">
        {items.map((b) => (
          <button
            key={b.id}
            data-testid={`blog-row-${b.id}`}
            onClick={() => open(b)}
            className="group w-full text-left bg-[#151515] border border-[#D8D6D0]/10 hover:border-[#D8D6D0]/30 transition-colors p-4 flex gap-5 items-center"
          >
            <img src={b.cover_image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${b.cover_image}` : b.cover_image} alt={b.title} className="w-32 h-20 object-cover flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-xl truncate">{b.title}</div>
              <div className="text-xs text-[#D8D6D0]/55 mt-1 truncate">{b.excerpt}</div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-[#D8D6D0]/40 mt-2">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(b.published_at).toLocaleDateString()}</span>
                <span>·</span>
                <span>{b.author}</span>
                {b.tags?.length > 0 && <><span>·</span><span>{b.tags.join(" · ")}</span></>}
              </div>
            </div>
            <Pencil className="w-4 h-4 text-[#D8D6D0]/30 group-hover:text-[#F5F3EF] flex-shrink-0" />
          </button>
        ))}
      </div>

      <SlidePanel
        open={!!editing}
        onClose={close}
        title={editing?.id ? "Edit Post" : "New Post"}
        subtitle={editing?.id ? `Slug: ${editing.slug}` : "Write a new Journal entry"}
        footer={
          <FormFooterActions
            onCancel={close}
            onSubmit={save}
            onDelete={editing?.id ? remove : null}
            submitting={saving}
          />
        }
      >
        {editing && (
          <>
            <TextField id="title" label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v, slug: editing.slug || slugify(v) })} required testId="blog-title" />
            <TextField id="slug" label="URL Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: slugify(v) })} hint="Auto-generated from title. Editable." />
            <TextField id="author" label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
            <TextareaField id="excerpt" label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} rows={2} required hint="Short summary shown on Blog index." />
            <TextareaField id="content" label="Content" value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} rows={12} hint="Plain text or Markdown. Line breaks preserved." />
            <ImageUpload label="Cover Image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} required testId="blog-cover" />
            <TextField id="tags" label="Tags (comma-separated)" value={tagsText} onChange={setTagsText} placeholder="design, construction, engineering" hint="Used for categorisation." />
          </>
        )}
      </SlidePanel>
    </div>
  );
}
